import { computed, onMounted, reactive, ref, toValue, watch, type MaybeRef } from 'vue'
import useGet from './api/useGet'
import type { CreateableObjective, Objective } from '@/core/composables/use-objectives'
import { useEventBus } from '@vueuse/core'
import { EventKey } from '@/core/events/event-key'
import usePost from '@/core/composables/api/usePost'
import { ApiRequestStatus } from '@/core/models/api/api-request-status'

export const useCoacheeObjectives = (coacheeId: MaybeRef<string>) => {
  const objectiveUpdatedBus = useEventBus(EventKey.objectiveUpdated)

  const coacheeIdValue = computed(() => toValue(coacheeId))

  const {
    data: rawData,
    load,
    status,
    reload,
  } = useGet<CoacheeObjectives>(`coaching/coachee/${coacheeIdValue.value}/objectives`)
  const { mutate: create, status: createStatus } = usePost<
    CreateableObjective,
    CreateableObjective
  >(`objectives`)

  const loadObjectives = () => load()
  const reloadObjectives = () => reload()

  const data = computed(() => rawData.value ?? { objectives: [] })

  const showForm = ref(false)
  const creatableObjective = reactive<Partial<CreateableObjective>>({
    isImpulse: false,
  })

  const resetCreatableObjective = () => {
    creatableObjective.title = undefined
    creatableObjective.description = undefined
    creatableObjective.feedbackTopicId = undefined
    creatableObjective.isImpulse = false
  }

  onMounted(() => loadObjectives())

  watch(coacheeIdValue, () => {
    loadObjectives()
    showForm.value = false
    resetCreatableObjective()
  })

  objectiveUpdatedBus.on(() => reloadObjectives())

  const createObjective = () => {
    showForm.value = true
  }

  const cancelCreateObjective = () => {
    showForm.value = false
    resetCreatableObjective()
  }

  const saveObjective = async () => {
    if (!creatableObjective.title) {
      return
    }

    await create({
      title: creatableObjective.title,
      description: creatableObjective.description,
      feedbackTopicId: creatableObjective.feedbackTopicId,
      ownerId: coacheeIdValue.value,
      isImpulse: false,
    })

    if (createStatus.value == ApiRequestStatus.Success) {
      cancelCreateObjective()
    }
    objectiveUpdatedBus.emit()
  }

  return {
    data,
    status,
    showForm,
    creatableObjective,
    createObjective,
    cancelCreateObjective,
    saveObjective,
  }
}

export interface CoacheeObjectives {
  readonly objectives: ReadonlyArray<Objective>
}
